import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import type { Demande } from '../../types';
import { formatDateCourt } from './format';
import { useFenetreModale } from './useFenetreModale';

// « 09:00:00 » → « 9 h », « 14:30:00 » → « 14 h 30 »
function heure(t?: string | null): string {
  const m = t ? /^(\d{1,2}):(\d{2})/.exec(t) : null;
  if (!m) return '';
  const h = Number(m[1]);
  return m[2] === '00' ? `${h} h` : `${h} h ${m[2]}`;
}

function horaire(d: Demande): string {
  const a = heure(d.atelier_heure_debut);
  const b = heure(d.atelier_heure_fin);
  return a && b ? `${a} – ${b}` : '';
}

/** « du lun. 14 sept. au ven. 18 sept. » ou « le lun. 14 sept. » (pour une phrase) */
function periodeEnPhrase(d: Demande): string {
  const debut = d.absence_debut ?? d.date_atelier;
  const fin = d.absence_fin ?? d.date_atelier;
  return debut === fin ? `le ${formatDateCourt(debut)}` : `du ${formatDateCourt(debut)} au ${formatDateCourt(fin)}`;
}

/** « le mardi 10 novembre », « du mardi 10 au jeudi 12 novembre », « du mardi 29 octobre au jeudi 5 novembre ». */
function periodeEnToutesLettres(d: Demande): string {
  const debut = d.absence_debut ?? d.date_atelier;
  const fin = d.absence_fin ?? d.date_atelier;
  const date = (iso: string, avecMois: boolean) => new Intl.DateTimeFormat('fr-CH', avecMois
    ? { weekday: 'long', day: 'numeric', month: 'long' } : { weekday: 'long', day: 'numeric' }).format(new Date(`${iso.slice(0, 10)}T12:00:00`));
  if (debut === fin) return `le ${date(debut, true)}`;
  const memeMois = debut.slice(0, 7) === fin.slice(0, 7);
  return `du ${date(debut, !memeMois)} au ${date(fin, true)}`;
}

/**
 * Vue intervenant·e : les brouillons repérés dans Tipee sont regroupés par
 * période d'absence — « Absence du lun. 14 au ven. 18 sept. : 5 ateliers
 * concernés » — avec un seul geste, « Envoyer mes N demandes ». Chaque séance
 * reste une demande individuelle (le regroupement est celui de l'affichage et
 * de l'envoi). Voir docs/DECISIONS.md §12.
 */
export function GroupesAbsenceTipee({ groupes, onChange }: { groupes: Demande[][]; onChange: () => void }) {
  const [confirmation, setConfirmation] = useState<Demande[] | null>(null);
  const [pourquoi, setPourquoi] = useState(false);
  // Échap referme l'explication (fenêtre par-dessus : on ne quitte jamais la page).
  useEffect(() => {
    if (!pourquoi) return;
    const surEchap = (e: KeyboardEvent) => { if (e.key === 'Escape') setPourquoi(false); };
    document.addEventListener('keydown', surEchap);
    return () => document.removeEventListener('keydown', surEchap);
  }, [pourquoi]);

  if (groupes.length === 0) return null;

  return (
    <div className="mb-16" style={{ display: 'grid', gap: 12 }}>
      {groupes.map((groupe) => {
        const n = groupe.length;
        const trie = [...groupe].sort((a, b) => a.date_atelier.localeCompare(b.date_atelier));
        return (
          <section key={`${groupe[0].absent_personne_id}-${groupe[0].absence_debut}`} className="carte-tipee" aria-label="Absence trouvée dans Tipee">
            <span className="carte-tipee-qui">Absence trouvée dans Tipee · À vous de jouer</span>
            <h3 className="carte-tipee-titre">
              Vous serez absent·e {periodeEnToutesLettres(groupe[0])} : {n} de vos ateliers {n > 1 ? 'ont' : 'a'} besoin d'un·e remplaçant·e
            </h3>
            <div className="carte-tipee-etapes">
              <div className="carte-tipee-etape carte-tipee-etape-faite"><b>✓ Dans Tipee</b><small>Votre absence est déclarée.</small></div>
              <span className="carte-tipee-fleche" aria-hidden="true">→</span>
              <div className="carte-tipee-etape carte-tipee-etape-afaire"><b>À faire ici</b><small>La demande de remplacement n'est pas encore envoyée.</small></div>
            </div>
            <ul className="carte-tipee-seances">
              {trie.map((d) => (
                <li key={d.id}>
                  <span><b>{formatDateCourt(d.date_atelier)}</b> · {d.atelier_nom}{horaire(d) ? ` · ${horaire(d)}` : ''}</span>
                </li>
              ))}
            </ul>
            <div className="flex items-center gap-16 flex-wrap">
              <button type="button" className="btn btn-accent" onClick={() => setConfirmation(groupe)}>
                {n > 1 ? `Envoyer mes ${n} demandes de remplacement` : 'Envoyer ma demande de remplacement'}
              </button>
              <button type="button" className="carte-tipee-lien" onClick={() => setPourquoi(true)}>Pourquoi je vois ceci ?</button>
            </div>
            <div className="text-xs text-ink-3">
              Chaque séance devient une demande. Vous trouverez ensuite {n > 1 ? 'vos remplaçant·e·s séance par séance' : 'votre remplaçant·e'}.
            </div>
          </section>
        );
      })}

      {pourquoi && (
        <div className="modal-overlay" onClick={() => setPourquoi(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="pourquoi-tipee-titre" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title" id="pourquoi-tipee-titre">Pourquoi je vois cette carte ?</h3>
              <button type="button" className="close-btn" onClick={() => setPourquoi(false)} aria-label="Fermer">✕</button>
            </div>
            <div className="modal-body">
              <p className="text-sm text-ink-1">
                Vous avez déclaré une absence dans <b>Tipee</b>, l'outil des congés. Quand cette absence tombe sur l'un de vos ateliers,
                le portail prépare pour vous une demande de remplacement par séance, afin que vous n'ayez rien à ressaisir.
              </p>
              <p className="text-sm text-ink-2" style={{ marginTop: 8 }}>
                <b>Rien n'est envoyé</b> tant que vous n'avez pas cliqué sur « Envoyer ». Une question sur l'une de ces séances ?
                L'équipe gestion peut vous aider.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-sm btn-accent" onClick={() => setPourquoi(false)} autoFocus>Compris</button>
            </div>
          </div>
        </div>
      )}

      {confirmation && (
        <ConfirmationEnvoiTipee demandes={confirmation} onFermer={() => setConfirmation(null)} onChange={onChange} />
      )}
    </div>
  );
}

/**
 * « Envoyer vos N demandes de remplacement ? » — la confirmation de l'envoi des brouillons repérés dans Tipee. Partagée entre la carte
 * « Absence trouvée dans Tipee » de la liste et le détail du jour du calendrier « Mes demandes » : même geste, mêmes mots.
 * `onChange` : recharge l'affichage (appelé après chaque envoi, même partiel) ; `onFermer` : la fenêtre se ferme (envoi réussi ou « Pas maintenant »).
 */
export function ConfirmationEnvoiTipee({ demandes, onFermer, onChange }: { demandes: Demande[]; onFermer: () => void; onChange: () => void }) {
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  // Une vraie fenêtre : le focus y entre (sur le choix sans conséquence), Échap la ferme, le focus revient là où il était.
  const pasMaintenant = useRef<HTMLButtonElement>(null);
  const { ref } = useFenetreModale(() => { if (!enCours) onFermer(); }, { focusInitial: () => pasMaintenant.current });

  async function envoyer() {
    setEnCours(true);
    setErreur(null);
    try {
      const res = await api.appliquerActionGroupee(demandes.map((d) => d.id), 'soumettre');
      if (res.echouees.length > 0) {
        setErreur(`${res.echouees.length} demande${res.echouees.length > 1 ? 's n\'ont' : ' n\'a'} pas pu être envoyée${res.echouees.length > 1 ? 's' : ''} : ${res.echouees[0].erreur}`);
      } else {
        onFermer();
      }
      onChange();
    } catch (err) {
      setErreur(err instanceof Error ? err.message : "L'envoi a échoué. Réessayez.");
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="envoi-tipee-titre" ref={ref} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title" id="envoi-tipee-titre">
            Envoyer {demandes.length > 1 ? `vos ${demandes.length} demandes` : 'votre demande'} de remplacement ?
          </h3>
          <button type="button" className="close-btn" onClick={onFermer} aria-label="Fermer" disabled={enCours}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--tipee-dim)', borderRadius: 'var(--r1)', padding: '10px 12px', marginBottom: 12 }}>
            <span className="font-medium" style={{ color: 'var(--tipee)' }}>Repérée dans Tipee.</span>{' '}
            Nous avons remarqué votre absence {periodeEnPhrase(demandes[0])} et préparé {demandes.length > 1 ? 'ces demandes' : 'cette demande'} pour vous.
          </div>
          <p className="text-sm text-ink-2">
            Une fois {demandes.length > 1 ? 'envoyées' : 'envoyée'}, il vous reste à trouver {demandes.length > 1 ? 'vos remplaçant·e·s' : 'votre remplaçant·e'}. L'équipe gestion vous aidera si besoin.
          </p>
          {erreur && <p className="text-sm" style={{ color: 'var(--danger)', marginTop: 12 }}>{erreur}</p>}
        </div>
        <div className="modal-footer">
          <button type="button" className="btn" ref={pasMaintenant} onClick={onFermer} disabled={enCours}>Pas maintenant</button>
          <button type="button" className="btn btn-accent" onClick={envoyer} disabled={enCours}>
            {enCours ? <span className="spinner spinner-sm" /> : demandes.length > 1 ? `Envoyer mes ${demandes.length} demandes` : 'Envoyer ma demande'}
          </button>
        </div>
      </div>
    </div>
  );
}
